import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { useCatalog } from "@/hooks/useCatalog";
import { fetchHerbs, money, img } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { ProductCardSkeleton } from "@/components/Skeleton";
import { GramCard, GramModal } from "@/components/GramShop";
import { LIFESTYLE } from "@/data/content";

const FILTERS = ["All", "Herbal", "Flowers", "Roots", "Berries", "Mint", "Caffeine-Free"];

const PAIRINGS = [
  { concept: "Ginger Root + Honey + Lemon", slug: "raw-organic-honey-8-oz" },
  { concept: "Chamomile + Honey", slug: "vanilla-bean-raw-honey" },
  { concept: "Peppermint + Honey", slug: "cinnamon-vanilla-raw-honey" },
  { concept: "Rose Hips + Honey", slug: "peach-mango-creamed-honey" },
  { concept: "Butterfly Pea + Honey + Lemon", slug: "vanilla-bean-whipped-honey" },
  { concept: "Elderberry + Honey", slug: "authentic-new-zealand-manuka-honey-mgo-234" },
];

export default function LooseHerbsBotanicals() {
  const { bySlug } = useCatalog();
  const { data: herbData, isLoading } = useQuery({ queryKey: ["herbs"], queryFn: fetchHerbs });
  const [filter, setFilter] = useState("All");
  const [modal, setModal] = useState(null);

  useEffect(() => { document.title = "Loose Herbs & Botanicals · Washington Family Provisions"; }, []);

  const herbs = herbData?.herbs || [];
  const filtered = filter === "All" ? herbs : herbs.filter((h) => h.tags.includes(filter));

  return (
    <div>
      <section className="relative grain overflow-hidden bg-sand">
        <img src={LIFESTYLE.honey} alt="Dried herbs and botanicals" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="relative z-[2] mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <Reveal>
            <nav className="mb-4 text-xs uppercase tracking-[0.2em] text-wood-500">
              <Link to="/" className="hover:text-honey-dark">Home</Link> / <span className="text-wood-900">Loose Herbs &amp; Botanicals</span>
            </nav>
            <h1 className="font-serif text-5xl text-wood-900 md:text-7xl">Loose Herbs &amp; Botanicals</h1>
            <p className="mt-4 max-w-2xl text-lg text-wood-700">Build your botanical pantry. Shop individual herbs, flowers, roots and berries in flexible quantities for teas, infusions, culinary creations and your own custom blends.</p>
          </Reveal>
        </div>
      </section>

      <div className="sticky top-[64px] z-30 border-b border-wood-900/10 bg-cream/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl gap-2 overflow-x-auto px-5 py-4 md:px-8" data-lenis-prevent>
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f.replace(/\s+/g, "-").toLowerCase()}`}
              className={`whitespace-nowrap rounded-full border px-5 py-2 text-sm font-medium transition-colors ${filter === f ? "border-wood-900 bg-wood-900 text-cream" : "border-wood-900/20 text-wood-700 hover:border-wood-900"}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <section className="mx-auto max-w-7xl px-5 py-16 md:px-8">
        {isLoading ? (
          <div className="grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-4 md:gap-6">{[...Array(6)].map((_, i) => <ProductCardSkeleton key={i} />)}</div>
        ) : (
          <div className="grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-4 md:gap-6">
            {filtered.map((h, idx) => (
              <Reveal key={h.id} delay={(idx % 4) * 0.05}>
                <GramCard product={h} onChoose={setModal} testid={`herb-card-${h.id}`} />
              </Reveal>
            ))}
          </div>
        )}
        {!isLoading && filtered.length === 0 && <p className="py-16 text-center text-lg text-wood-500">Nothing in this filter yet — try another.</p>}
      </section>

      <section className="bg-honey/15" data-testid="pair-with-honey">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-honey-dark">Culinary inspiration</p>
            <h2 className="mt-3 font-serif text-4xl text-wood-900 md:text-5xl">Pair It With Honey</h2>
            <p className="mt-3 max-w-xl text-lg text-wood-700">A spoonful of Washington Family Provisions honey turns a simple brew into a ritual. A few of our favourite combinations to try.</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-6">
            {PAIRINGS.map((p, i) => {
              const honey = bySlug(p.slug);
              if (!honey) return null;
              return (
                <Reveal key={p.slug} delay={i * 0.05}>
                  <Link to={`/product/${honey.slug}`} data-testid={`pairing-${honey.slug}`} className="group flex h-full flex-col rounded-2xl bg-cream p-4 transition-shadow hover:shadow-lg">
                    <div className="overflow-hidden rounded-xl bg-sand">
                      <img src={img(honey.images[0], 400)} alt={honey.name} className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <p className="mt-3 font-serif text-base leading-tight text-wood-900">{p.concept}</p>
                    <span className="mt-1 inline-flex items-center gap-1 text-xs text-honey-dark">with {honey.name} <ArrowRight size={12} /></span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {modal && <GramModal product={modal} onClose={() => setModal(null)} />}
    </div>
  );
}
